import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useProducts } from '@/modules/products/hooks/useProducts';
import { usePlans } from '@/modules/plans/hooks/usePlans';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import type { SubscriptionStatus } from '@/modules/subscriptions/types';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef } from '@/shared/components/ui/DataTable';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { Input } from '@/shared/components/ui/input';
import { EntitlementStatusBadge } from '../components/EntitlementStatusBadge';
import { TrialDaysBadge } from '../components/TrialDaysBadge';
import { TrialDetailDrawer } from '../components/TrialDetailDrawer';
import { TrialMobileCard } from '../components/TrialMobileCard';
import { TrialTruncatedText } from '../components/TrialTruncatedText';
import { listAdminTrials } from '../services/trials.api';
import type { AdminTrialItem, ListAdminTrialsQuery } from '../types/adminTrial';
import { buildTrialCustomerIdentity } from '../types/trialCustomerIdentity';
import { EXPIRING_SOON_DAYS, computeTrialKpis, formatDate } from '../utils/trialHelpers';

const TRIAL_STATUSES: SubscriptionStatus[] = [
  'trialing',
  'suspended',
  'expired',
  'cancelled',
  'active',
];

function KpiSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-5 animate-pulse">
      <div className="h-3 w-24 rounded bg-muted" />
      <div className="mt-3 h-8 w-16 rounded bg-muted" />
    </div>
  );
}

export function TrialsListPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | ''>('');
  const [productFilter, setProductFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [expiringSoonOnly, setExpiringSoonOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTrial, setSelectedTrial] = useState<AdminTrialItem | null>(null);

  const canSuspend = user?.permissions.includes('subscriptions.suspend') ?? false;
  const canCancel = user?.permissions.includes('subscriptions.cancel') ?? false;
  const canUpdate = user?.permissions.includes('subscriptions.update') ?? false;

  const serverQuery = useMemo((): ListAdminTrialsQuery => {
    const q: ListAdminTrialsQuery = { limit: 100 };
    if (statusFilter) q.status = statusFilter;
    if (productFilter) q.product_id = productFilter;
    if (planFilter) q.plan_id = planFilter;
    if (dateFrom) q.from = new Date(dateFrom).toISOString();
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      q.to = end.toISOString();
    }
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    if (expiringSoonOnly) q.expiring_soon_days = EXPIRING_SOON_DAYS;
    return q;
  }, [
    statusFilter,
    productFilter,
    planFilter,
    dateFrom,
    dateTo,
    debouncedSearch,
    expiringSoonOnly,
  ]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'trials', 'list', serverQuery],
    queryFn: () => listAdminTrials(serverQuery),
  });

  const { data: productsData } = useProducts({ limit: 100 });
  const { data: plansData } = usePlans({ limit: 100 });

  const products = useMemo(() => productsData?.items ?? [], [productsData?.items]);
  const plans = useMemo(() => plansData?.items ?? [], [plansData?.items]);

  const trials = useMemo(() => data?.items ?? [], [data?.items]);
  const kpis = useMemo(() => computeTrialKpis(trials), [trials]);

  const applySearch = () => {
    setDebouncedSearch(searchQuery);
  };

  const unavailable = t('trials.identity.unavailable');

  const columns: ColumnDef<AdminTrialItem>[] = useMemo(
    () => [
      {
        key: 'customerName',
        header: t('trials.columns.customerName'),
        accessor: (trial) => (
          <TrialTruncatedText
            value={trial.customer_name}
            fallback={unavailable}
            maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
            className="font-medium"
          />
        ),
        sortValue: (trial) => trial.customer_name ?? '',
      },
      {
        key: 'tenant',
        header: t('trials.columns.tenant'),
        accessor: (trial) => (
          <TrialTruncatedText
            value={trial.tenant_name}
            fallback={unavailable}
            maxWidthClass="max-w-[10rem] lg:max-w-[14rem]"
          />
        ),
        sortValue: (trial) => trial.tenant_name,
      },
      {
        key: 'customerEmail',
        header: t('trials.columns.customerEmail'),
        accessor: (trial) => (
          <TrialTruncatedText
            value={trial.customer_email}
            fallback={unavailable}
            maxWidthClass="max-w-[11rem] lg:max-w-[15rem]"
          />
        ),
        sortValue: (trial) => trial.customer_email ?? '',
      },
      {
        key: 'customerPhone',
        header: t('trials.columns.customerPhone'),
        accessor: (trial) => (
          <TrialTruncatedText
            value={trial.customer_phone}
            fallback={unavailable}
            maxWidthClass="max-w-[9rem]"
            nowrap
          />
        ),
        sortValue: (trial) => trial.customer_phone ?? '',
      },
      {
        key: 'product',
        header: t('trials.columns.product'),
        accessor: (trial) => (
          <TrialTruncatedText
            value={trial.product_name}
            fallback={unavailable}
            maxWidthClass="max-w-[8rem]"
          />
        ),
        sortValue: (trial) => trial.product_name,
      },
      {
        key: 'plan',
        header: t('trials.columns.plan'),
        accessor: (trial) => (
          <TrialTruncatedText
            value={trial.plan_name}
            fallback={unavailable}
            maxWidthClass="max-w-[8rem]"
          />
        ),
        sortValue: (trial) => trial.plan_name,
      },
      {
        key: 'status',
        header: t('trials.columns.trialStatus'),
        accessor: (trial) => (
          <div className="whitespace-nowrap">
            <SubscriptionStatusBadge status={trial.subscription_status} />
          </div>
        ),
        sortValue: (trial) => trial.subscription_status,
        align: 'center',
      },
      {
        key: 'entitlement',
        header: t('trials.columns.entitlementStatus'),
        accessor: (trial) => (
          <div className="whitespace-nowrap">
            <EntitlementStatusBadge status={trial.entitlement_status} />
          </div>
        ),
        sortValue: (trial) => trial.entitlement_status ?? '',
        align: 'center',
      },
      {
        key: 'daysRemaining',
        header: t('trials.columns.daysRemaining'),
        accessor: (trial) => (
          <div className="whitespace-nowrap flex justify-center">
            <TrialDaysBadge trial={trial} />
          </div>
        ),
        sortValue: (trial) => trial.trial_ends_at ?? '',
        align: 'center',
      },
      {
        key: 'trialStart',
        header: t('trials.columns.trialStart'),
        accessor: (trial) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDate(trial.trial_started_at, locale)}
          </span>
        ),
        sortValue: (trial) => trial.trial_started_at,
      },
      {
        key: 'trialEnd',
        header: t('trials.columns.trialEnd'),
        accessor: (trial) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDate(trial.trial_ends_at, locale)}
          </span>
        ),
        sortValue: (trial) => trial.trial_ends_at ?? '',
      },
      {
        key: 'actions',
        header: t('trials.columns.actions'),
        accessor: (trial) => (
          <div className="flex justify-end whitespace-nowrap shrink-0">
            <Button variant="outline" size="sm" onClick={() => setSelectedTrial(trial)}>
              <Icons.Eye className="h-3.5 w-3.5" />
              {t('common.view')}
            </Button>
          </div>
        ),
        align: 'right',
      },
    ],
    [t, locale, unavailable],
  );

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('trials.title')}
          subtitle={t('trials.description')}
          icon={<Icons.Timer className="h-7 w-7" />}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <KpiSkeleton key={i} />
          ))}
        </div>
        <LoadingPanel />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('trials.title')}
          subtitle={t('trials.description')}
          icon={<Icons.Timer className="h-7 w-7" />}
        />
        <ErrorPanel message={t('trials.error.loadFailed')} />
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => void refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHero
        title={t('trials.title')}
        subtitle={t('trials.description')}
        icon={<Icons.Timer className="h-7 w-7" />}
        eyebrow={t('trials.eyebrow')}
        metrics={[
          { label: t('trials.kpi.total'), value: String(kpis.total) },
          { label: t('trials.kpi.active'), value: String(kpis.active) },
          { label: t('trials.kpi.expiringSoon'), value: String(kpis.expiringSoon) },
        ]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            isLoading={isFetching}
            aria-label={t('trials.actions.refresh')}
          >
            <Icons.RefreshCw className="h-4 w-4" />
            {t('trials.actions.refresh')}
          </Button>
        }
      />

      <p className="text-xs text-muted-foreground -mt-2">{t('trials.kpi.loadedResultsNote')}</p>

      <KpiGrid className="gap-4 xl:grid-cols-3 2xl:grid-cols-6">
        <KpiCard
          title={t('trials.kpi.total')}
          value={String(kpis.total)}
          tone="blue"
          icon={<Icons.Timer className="h-4 w-4" />}
        />
        <KpiCard
          title={t('trials.kpi.active')}
          value={String(kpis.active)}
          tone="green"
          icon={<Icons.CheckCircle className="h-4 w-4" />}
        />
        <KpiCard
          title={t('trials.kpi.suspended')}
          value={String(kpis.suspended)}
          tone="violet"
          icon={<Icons.Ban className="h-4 w-4" />}
        />
        <KpiCard
          title={t('trials.kpi.expiringSoon')}
          value={String(kpis.expiringSoon)}
          tone="amber"
          hint={t('trials.kpi.expiringSoonHint', { days: EXPIRING_SOON_DAYS })}
          icon={<Icons.Timer className="h-4 w-4" />}
        />
        <KpiCard
          title={t('trials.kpi.expired')}
          value={String(kpis.expired)}
          tone="rose"
          icon={<Icons.XCircle className="h-4 w-4" />}
        />
        <KpiCard
          title={t('trials.kpi.cancelled')}
          value={String(kpis.cancelled)}
          tone="cyan"
          {...(kpis.avgRemainingDays !== null
            ? { hint: t('trials.kpi.avgRemaining', { days: kpis.avgRemainingDays }) }
            : {})}
          icon={<Icons.Repeat className="h-4 w-4" />}
        />
      </KpiGrid>

      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <label htmlFor="trial-search" className="text-xs font-medium text-muted-foreground">
              {t('trials.filters.search')}
            </label>
            <div className="flex gap-2">
              <Input
                id="trial-search"
                placeholder={t('trials.filters.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applySearch();
                }}
              />
              <Button type="button" variant="secondary" size="sm" onClick={applySearch}>
                {t('trials.filters.searchApply')}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="trial-status" className="text-xs font-medium text-muted-foreground">
              {t('common.status')}
            </label>
            <select
              id="trial-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SubscriptionStatus | '')}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('common.allStatuses')}</option>
              {TRIAL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`subscriptions.status.${s}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="trial-product" className="text-xs font-medium text-muted-foreground">
              {t('trials.filters.product')}
            </label>
            <select
              id="trial-product"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('trials.filters.allProducts')}</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {i18n.language === 'ar' ? p.name_ar || p.name_en : p.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="trial-plan" className="text-xs font-medium text-muted-foreground">
              {t('trials.filters.plan')}
            </label>
            <select
              id="trial-plan"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('trials.filters.allPlans')}</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {i18n.language === 'ar' ? p.name_ar || p.name_en : p.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="trial-from" className="text-xs font-medium text-muted-foreground">
              {t('trials.filters.dateFrom')}
            </label>
            <input
              id="trial-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="space-y-1.5 min-w-[140px]">
            <label htmlFor="trial-to" className="text-xs font-medium text-muted-foreground">
              {t('trials.filters.dateTo')}
            </label>
            <input
              id="trial-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none pb-2">
            <input
              type="checkbox"
              checked={expiringSoonOnly}
              onChange={(e) => setExpiringSoonOnly(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            {t('trials.filters.expiringSoon')}
          </label>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('trials.filters.hint', { days: EXPIRING_SOON_DAYS })}
        </p>
      </div>

      {trials.length === 0 ? (
        <EmptyPanel title={t('trials.empty.title')} message={t('trials.empty.message')} />
      ) : (
        <>
          <div className="hidden md:block min-w-0">
            <DataTable
              data={trials}
              columns={columns}
              rowKey={(trial) => trial.subscription_id}
              total={data?.total ?? trials.length}
              initialPageSize={10}
              emptyMessage={t('trials.empty.title')}
            />
          </div>

          <div className="md:hidden space-y-3 min-w-0">
            {trials.map((trial) => (
              <TrialMobileCard
                key={trial.subscription_id}
                trial={trial}
                locale={locale}
                onViewDetails={() => setSelectedTrial(trial)}
              />
            ))}
          </div>
        </>
      )}

      {data?.total !== undefined ? (
        <p className="text-xs text-muted-foreground">
          {t('trials.footer.showing', {
            shown: trials.length,
            total: data.total,
          })}
        </p>
      ) : null}

      {selectedTrial ? (
        <TrialDetailDrawer
          trial={selectedTrial}
          identity={buildTrialCustomerIdentity(selectedTrial)}
          canSuspend={canSuspend}
          canCancel={canCancel}
          canUpdate={canUpdate}
          onClose={() => setSelectedTrial(null)}
          onTrialUpdated={(updated) => setSelectedTrial(updated)}
        />
      ) : null}
    </div>
  );
}
